import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal, { Footer } from 'src/components/modals/modal';
import { useDispatch, useSelector } from 'src/store/store';
import { helpScoutArticle } from 'src/utils/helpScoutBeacon';
import i18n from 'src/utils/translate';
import { fetchProfile } from 'src/modules/user/shared/actions';
import { addConditions } from 'src/modules/user/shared/conditionActions';
import Icon from 'src/components/icon';
import { LinkButton } from 'styles-js/buttons';
import { closeModal, showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { titleSmall } from 'styles-js/mixins/typography';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event } from 'src/constants/footfall';
import Button from 'src/components/buttons/button';
import { useIsFreePost } from 'src/modules/community/singlePost/singlePostPage';
import { fetchCommunityTopTags } from 'src/modules/community/communityPosts/actions';
import { useIsCommunityMember } from 'src/utils/context';
import { getLocalStorageItem, Key, setLocalStorageItem } from 'src/utils/localStorage';

const formatCondition = ({ entityId }: { entityId: number }) => ({
  entityId,
  isPrivate: false,
  selectedConditionReason: 'have',
});

type TopCondition = {
  entityId: number;
  slug: string;
  name: string;
  concept: 'CONDITION';
  rank: number;
}

export default function CommunityTopConditionsModal() {
  const dispatch = useDispatch();
  const conditions = useSelector(({ modal }) => modal.modalProps.topConditions as TopCondition[]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  useEffect(() => {
    trackFootfallEvent(Event.CLICKED, { clickType: 'modal-rank1-conditions-viewed' });
  }, []);

  const onChange = (slug: string) => {
    if (selectedConditions.includes(slug)) {
      setSelectedConditions(selectedConditions.filter(c => c !== slug));
    } else {
      setSelectedConditions([...selectedConditions, slug]);
    }
  };

  const onAccept = async () => {
    trackFootfallEvent(Event.CLICKED, { clickType: 'modal-rank1-conditions-added' });
    if (selectedConditions.length > 0) {
      const conditionsToAdd = conditions.filter(({ slug }) => selectedConditions.includes(slug)).map(formatCondition);
      await dispatch(addConditions(conditionsToAdd));
    }
    await dispatch(closeModal());
    dispatch(showModal({ modalType: ModalType.ProfileSurvey, modalProps: { screenOverride: [
      'Conditions',
      'ConditionDetails',
      'WellbeingInterests',
      'Gender',
      'Country',
      'Ethnicity',
      'DateOfBirth',
      'Bio',
      'Complete',
    ] } }));
  };

  const onClose = () => {
    trackFootfallEvent(Event.CLICKED, { clickType: 'modal-rank1-conditions-closed' });
  };

  const CustomFooter = () => (
    <Footer>
      <Button disabled={selectedConditions.length === 0 && !isOtherSelected} onClick={onAccept}>
        {i18n.t('Add')}
      </Button>
    </Footer>
  );

  return (
    <Modal
      customFooter={<CustomFooter />}
      title={i18n.t('Do you have any of these conditions?')}
      onClose={onClose}
    >
      <Subtitle>{i18n.t('Top conditions added by members')}</Subtitle>
      <FieldLabel>{i18n.t('Please select all that apply:')}</FieldLabel>
      {conditions.map((condition) => (
        <Label key={condition.slug}>
          <input type="checkbox" onChange={() => onChange(condition.slug)} />
          {condition.name}
        </Label>
      ))}
      <Label>
        <input type="checkbox" onChange={() => setIsOtherSelected(!isOtherSelected)} />
        {i18n.t('Other')}
      </Label>
      <Description>{i18n.t('Answer to add to your profile')}</Description>
      <LinkButton onClick={() => helpScoutArticle('101-your-profile-on-healthunlocked')}>
        {i18n.t('Learn more')}<Icon icon="open-right" />
      </LinkButton>
    </Modal>
  );
}

export const useTopConditionModal = (): void => {
  const dispatch = useDispatch();
  const topTags = useSelector(({ pages }) => pages.communityPosts?.topTags);
  const topConditions = topTags?.filter(({ rank, concept }) => rank === 1 && concept === 'CONDITION');
  const userConditions = useSelector(({ currentUserProfile }) => currentUserProfile.conditions);
  const communityId = useSelector(({ context }) => context.currentCommunity?.communityId);
  const { currentUser } = useSelector(({ context }) => context);
  const modalType = useSelector(({ modal }) => modal.modalType);
  const isFreePost = useIsFreePost();
  const [isModalViewed, setIsModalViewed] = useState(true);

  useEffect(() => {
    if (modalType || isModalViewed || isFreePost || !currentUser || !communityId) return;
    Promise.all([
      dispatch(fetchCommunityTopTags(communityId)),
      dispatch(fetchProfile()),
    ]);
  }, [dispatch, communityId, isFreePost, currentUser, isModalViewed, modalType]);

  useEffect(() => {
    if (!communityId) return setIsModalViewed(true);
    const communityIds = JSON.parse(getLocalStorageItem(Key.communityTopConditionsViewed) || '[]');
    setIsModalViewed(communityIds.includes(communityId));
  }, [communityId]);

  const userHasTopCondition = useMemo(() => {
    if (!topConditions || !userConditions || topConditions.length === 0) return true;
    const topSlugs = topConditions.map(({ slug }) => slug);
    const userSlugs = userConditions.map(({ slug }) => slug);
    return topSlugs.some((top) => userSlugs.includes(top));
  }, [topConditions, userConditions]);

  const isCommunityMember = useIsCommunityMember();

  useEffect(() => {
    if (userHasTopCondition || isModalViewed || !communityId || !isCommunityMember || modalType) return;
    setIsModalViewed(true);
    const communityIds = JSON.parse(getLocalStorageItem(Key.communityTopConditionsViewed) || '[]');
    setLocalStorageItem(Key.communityTopConditionsViewed, JSON.stringify([...communityIds, communityId]));
    dispatch(showModal({ modalType: ModalType.CommunityTopConditions, modalProps: { topConditions } }));
  }, [userHasTopCondition, dispatch, communityId, topConditions, isCommunityMember, isModalViewed, modalType]);
};

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colorTextLight};
  margin-bottom: 20px;
`;
const FieldLabel = styled.p`
  ${titleSmall}
  margin-bottom: 10px;
`;
const Label = styled.label`
  display: block;
  input {
    margin-right: 10px;
  }
`;
const Description = styled.p`
  margin-top: 40px;
`;
